import React from "react";
import { Button, Dialog } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { DialogHeaderWithClose } from "../../../../components/Dialog/DialogHeaderWithClose.component";
import { SeeditDialogActions } from "../../../../components/Dialog/SeeditDialogActions.component";
import { SeeditDialogContent } from "../../../../components/Dialog/SeeditDialogContent.component";
import { useInquiryCount } from "../../../../components/Menu/hooks/use-inquiry-count";
import { updateColleagues } from "../../../../utils/axios-requests";
import { Colleague } from "../../types/Colleague.type";
import { ColleagueCardPermissionSwitchGroup } from "./colleague-card-perm-switch-group.component";

type P = {
  isOpen: boolean;
  isInquiry: boolean;
  /** original value of colleague */
  colleague: Colleague;
  /** editing colleague value */
  editedColleague: Colleague;
  onClose: () => void;
  onRefetch: () => void;
  setEditedColleague: (colleague: Colleague) => void;
};

export const ColleagueCardPermissionsDialog: React.FC<P> = ({
  isOpen,
  colleague,
  editedColleague,
  isInquiry,
  setEditedColleague,
  onClose,
  onRefetch
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { inquiryRefetch } = useInquiryCount();

  const enqueueErrorSnackbar = (err: any) => {
    /** do not allow to delete connection who is not allowed to due to auto connection rule */
    const notAllowToDisconnectByAutoConnectionRule =
      "Your connection changes are not aligned with relevant auto connection rule";

    if (err.response.data.message === notAllowToDisconnectByAutoConnectionRule) {
      enqueueSnackbar(t("_colleagueNotAllowToDisconnectByAutoConnectionRule"), {
        variant: "error"
      });
    } else enqueueSnackbar(t("There was an error updating connection."), { variant: "error" });

    onRefetch();
  };

  const handleTeamBookingColleagueChange = () => {
    const updatedUser = { ...editedColleague };
    if (!updatedUser.teamBookingReqColleague) updatedUser.teamBookingColleague = false;
    updatedUser.teamBookingReqColleague = !editedColleague.teamBookingReqColleague;

    setEditedColleague(updatedUser);
  };

  const handleTeamBookingPermissionChange = () => {
    const updatedUser = { ...editedColleague };

    updatedUser.teamBookingPermission = !editedColleague.teamBookingPermission;
    updatedUser.teamBookingReqPermission = updatedUser.teamBookingPermission;

    if (updatedUser.teamBookingPermission) {
      updatedUser.friendsReqPermission = true;
      updatedUser.friendsPermission = true;
    }
    setEditedColleague(updatedUser);
  };

  const handleScheduleColleagueChange = () => {
    const updatedUser = { ...editedColleague };

    if (!updatedUser.scheduleReqColleague) updatedUser.scheduleColleague = false;

    updatedUser.scheduleReqColleague = !editedColleague.scheduleReqColleague;

    setEditedColleague(updatedUser);
  };

  const handleSchedulePermissionChange = () => {
    const updatedUser = { ...editedColleague };

    updatedUser.schedulePermission = !editedColleague.schedulePermission;
    updatedUser.scheduleReqPermission = updatedUser.schedulePermission;
    if (updatedUser.schedulePermission) {
      updatedUser.friendsReqPermission = true;
      updatedUser.friendsPermission = true;
    }

    setEditedColleague(updatedUser);
  };

  const handleSubmit = () => {
    const updatedUser = { ...editedColleague };
    if (!updatedUser.scheduleReqColleague) updatedUser.scheduleColleague = false;
    if (!updatedUser.teamBookingReqColleague) updatedUser.teamBookingColleague = false;
    updatedUser.friendsColleague = true;
    updatedUser.friendsReqColleague = true;

    updateColleagues([updatedUser])
      .then(() => {
        setEditedColleague(updatedUser);
        inquiryRefetch();
        onClose();
      })
      .catch(err => {
        // when catch err, turn back to original value
        setEditedColleague(colleague);
        enqueueErrorSnackbar(err);
        onRefetch();
      });
  };

  const handleSubmitInquiry = () => {
    const updatedUser = { ...editedColleague };
    updatedUser.friendsReqPermission = true;
    updatedUser.friendsPermission = true;
    updateColleagues([updatedUser])
      .then(() => {
        inquiryRefetch();
        onClose();
      })
      .catch(err => {
        // when catch err, turn back to original value
        setEditedColleague(colleague);
        enqueueErrorSnackbar(err);
        onRefetch();
      });
  };

  return (
    <Dialog
      aria-labelledby="colleague-card-permissions-dialog-title"
      data-testid="colleague-card-permissions-dialog"
      onClose={onClose}
      open={isOpen}
    >
      <DialogHeaderWithClose
        closeTestId="btn-close"
        onClose={onClose}
        title={t("Adjust permissions")}
        titleId="colleague-card-permissions-dialog-title"
      />
      <SeeditDialogContent data-testid="restrict-time-relat-perm">
        <ColleagueCardPermissionSwitchGroup
          colleague={editedColleague}
          isInquiry={isInquiry}
          handleTeamBookingColleagueChange={handleTeamBookingColleagueChange}
          handleTeamBookingPermissionChange={handleTeamBookingPermissionChange}
          handleScheduleColleagueChange={handleScheduleColleagueChange}
          handleSchedulePermissionChange={handleSchedulePermissionChange}
        />
      </SeeditDialogContent>
      <SeeditDialogActions>
        <Button
          color="primary"
          data-testid="colleague-permissions-dialog-submit"
          onClick={isInquiry ? handleSubmitInquiry : handleSubmit}
          variant="contained"
        >
          {t("Accept")}
        </Button>
      </SeeditDialogActions>
    </Dialog>
  );
};
